import type { BodyHandle, ContactHitEvent } from 'box3d-web';
import {
  BLAST_KINETIC_MUL,
  BLAST_MAX_LAUNCH,
  blastRadiusScale,
  bombEvent,
  CANNON_FRACTURE_ENERGY_TRANSFER,
  cannonballEvent,
  concussionEvent,
  IMPULSE_ENERGY_CAP_J,
  weaponImpactEvent,
  type DamageEvent,
  type ResolveResult,
} from '../../services/damageModel';
import { projectImpactOntoWall, type DamageFact } from './demolitionDamageLedger';
import { firstFracture, subsequentCarve, resolvedSeverity, clampDamage } from './fractureOrchestrator';
import {
  bodyPosition,
  bodyPositionLive,
  clampLaunchSpeeds,
  embedSpikeProjectile,
  ejectProjectileFromWallFace,
  kickDynamicRubble,
  markRubbleActivity,
  projectileDamageMass,
  projectileSpecMass,
  rebuildBuffers,
  retireProjectile,
  spawnProjectile,
  stabilizeProjectileContact,
} from './debrisPool';
import { appendDemolitionEvent, pushImpactTrace, queueImpactBurst, recordImpact } from './events';
import {
  BLAST_EQUIV_SPEED,
  BLAST_FUSE_SEC,
  BLAST_STAGGER_SEC,
  BLAST_VOLLEY_SUB_BUDGET,
  DEFAULT_WEAPON_POWER,
  FEEDBACK_SPEED_THRESHOLD,
  MAX_CHARGES,
  RUBBLE_TRANSMISSION_DEPTH,
  RUBBLE_TRANSMISSION_TRANSFER,
  SUBSEQUENT_IMPACT_SUB_BUDGET,
  clampWeaponPower,
} from './demolitionTuning';
import type {
  CarveOutcome,
  DemoTool,
  DemolitionCharge,
  DemolitionContactBatch,
  DemolitionImpact,
  DemolitionRuntime,
  ImpactContactKind,
} from './types';

/**
 * Live kinetic fracture-energy transfer by weapon. Cannon is scaled so a full
 * muzzle-speed contact lands in the calibrated 28 m/s energy band without
 * retuning material strengths. Spike/rubble stay at full ½mv².
 */
export function kineticFractureTransfer(weapon: DemoTool): number {
  switch (weapon) {
    case 'cannon':
      return CANNON_FRACTURE_ENERGY_TRANSFER;
    case 'spike':
    case 'rubble':
      return 1;
    default:
      return 1;
  }
}

/** Append one canonical accepted-damage fact and mirror it into the event ring. */
export function recordDamageFact(
  rt: DemolitionRuntime,
  impact: DemolitionImpact,
  event: DamageEvent,
  outcome: CarveOutcome,
  result: ResolveResult | null,
  damageBefore: number,
): DamageFact {
  const direction = event.direction.kind === 'focused'
    ? event.direction.vector
    : { x: 0, y: 0, z: 1 };
  const length = Math.hypot(direction.x, direction.y, direction.z) || 1;
  const outwardNormal = {
    x: -direction.x / length,
    y: -direction.y / length,
    z: -direction.z / length,
  };
  let delta = 0;
  if (result) {
    for (const update of result.rimUpdates) delta = Math.max(delta, update.delta);
    for (const deposit of result.deposits) {
      if (deposit.thresholdE <= 0 || deposit.failed) continue;
      delta = Math.max(delta, (deposit.energy / deposit.thresholdE) * rt.profile.physics.damageAccumRate * 0.25);
    }
  }
  const restPosition = {
    x: impact.x - rt.wallCenter.x,
    y: impact.y - rt.wallCenter.y,
    z: impact.z - rt.wallCenter.z,
  };
  const incoming = event.direction.kind === 'focused'
    ? event.direction.vector
    : { x: 0, y: 0, z: 0 };
  const projected = projectImpactOntoWall(restPosition, incoming, rt.wallHalf);
  const fact = rt.damageLedger.append({
    simTime: rt.stepper.simTime,
    kind: event.footprint.kind === 'sphere' ? 'blast' : 'kinetic',
    position: restPosition,
    outwardNormal,
    radius: event.footprint.kind === 'sphere' ? event.footprint.radius : event.footprint.effectiveRadius,
    appliedEnergy: outcome.appliedEnergy,
    normalizedSeverity: resolvedSeverity(rt, event, result),
    cellId: outcome.preferredPrimaryCellId ?? result?.carvedCellIds[0] ?? result?.rimUpdates[0]?.cellId,
    damageBefore,
    damageAfter: outcome.topologyChanged ? 1 : clampDamage(damageBefore + delta),
    topologyChanged: outcome.topologyChanged,
    impactTangent: projected.tangent,
    impactGrazing: projected.grazing,
    pulverizedVolume: rt.lastPulverizedVolume,
  });
  appendDemolitionEvent(rt, 'damage-fact', fact);
  return fact;
}

/**
 * Classify a body as wall (monolith or cell record). Distinguishes static
 * remnant (structural target) from dynamic rubble (physics-only bounce).
 */
export function classifyWallHit(
  rt: DemolitionRuntime,
  otherId: number,
): { hitsWall: boolean; hitStaticRemnant: boolean; preferredCellId?: number } {
  if (!rt.fractured && otherId === rt.monolithBodyId) {
    return { hitsWall: true, hitStaticRemnant: true };
  }
  if (rt.fractured) {
    for (const rec of rt.records.values()) {
      if (rec.bodyId !== otherId) continue;
      if (rec.state === 'static') {
        return {
          hitsWall: true,
          hitStaticRemnant: true,
          preferredCellId: rec.cellId,
        };
      }
      if (rec.state === 'dynamic') {
        return { hitsWall: true, hitStaticRemnant: false };
      }
      break;
    }
  }
  return { hitsWall: false, hitStaticRemnant: false };
}

/** Orient ContactHitEvent.normal so it points from the projectile toward other. */
export function orientHitNormal(
  hit: ContactHitEvent,
  ballId: number,
): { x: number; y: number; z: number } {
  // Native normal points bodyA → bodyB. Flip when the projectile is bodyB.
  const sign = hit.bodyA === ballId ? 1 : -1;
  return {
    x: hit.normal.x * sign,
    y: hit.normal.y * sign,
    z: hit.normal.z * sign,
  };
}

/**
 * Contact → fracture. ContactHitEvent is authoritative: point, oriented normal,
 * and approachSpeed drive damage. Never reads post-solver getLinearVelocity for
 * fracture energy. Must run after every fixed world.step so multi-step frames
 * cannot overwrite kinematics before we drain hits.
 */
export function processContacts(rt: DemolitionRuntime, batch: DemolitionContactBatch) {
  const threshold = rt.profile.impactSpeedThreshold;
  // Scene owner already drained Box3D; consume the frozen batch only.
  const beginEvents = batch.begins;
  const hitEvents = batch.hits;

  type ContactCand = {
    ballId: number;
    otherId: number;
    speed: number;
    point: { x: number; y: number; z: number };
    /** Unit direction projectile → other (focused velocity uses this × speed). */
    dir: { x: number; y: number; z: number };
    hit: ContactHitEvent | undefined;
    fromHit: boolean;
    /** Selection priority: 2 wall/rubble, 1 other projectile, 0 ground. */
    rank?: number;
  };

  // Strongest relevant hit per projectile this step (speed desc, body-id tiebreak).
  const bestByBall = new Map<number, ContactCand>();

  const consider = (cand: ContactCand) => {
    if (cand.speed < FEEDBACK_SPEED_THRESHOLD) return;
    const aIsBall = rt.cannonballSet.has(cand.ballId);
    if (!aIsBall) return;
    if (rt.cannonballSet.has(cand.otherId)) {
      cand.rank = 1;
    } else if (cand.otherId === rt.groundBodyId) {
      cand.rank = 0;
    } else {
      const wall = classifyWallHit(rt, cand.otherId);
      if (!wall.hitsWall) return;
      cand.rank = 2;
    }

    const prev = bestByBall.get(cand.ballId);
    if (!prev) {
      bestByBall.set(cand.ballId, cand);
      return;
    }
    // Prefer hard-hit payloads over begin-only fallbacks.
    if (cand.fromHit && !prev.fromHit) {
      bestByBall.set(cand.ballId, cand);
      return;
    }
    if (!cand.fromHit && prev.fromHit) return;
    // Wall/rubble beats projectile beats ground, regardless of speed: a ball
    // skimming the ground while brushing rubble must register the rubble.
    const candRank = cand.rank ?? 0;
    const prevRank = prev.rank ?? 0;
    if (candRank !== prevRank) {
      if (candRank > prevRank) bestByBall.set(cand.ballId, cand);
      return;
    }
    if (cand.speed > prev.speed) {
      bestByBall.set(cand.ballId, cand);
      return;
    }
    if (cand.speed < prev.speed) return;
    // Stable tie-break: lower other body id, then lower ball id.
    if (
      cand.otherId < prev.otherId ||
      (cand.otherId === prev.otherId && cand.ballId < prev.ballId)
    ) {
      bestByBall.set(cand.ballId, cand);
    }
  };

  for (const hit of hitEvents) {
    const aIsBall = rt.cannonballSet.has(hit.bodyA);
    const bIsBall = rt.cannonballSet.has(hit.bodyB);
    if (!aIsBall && !bIsBall) continue;
    if (aIsBall && bIsBall) {
      for (const ballId of [hit.bodyA, hit.bodyB]) {
        const otherId = ballId === hit.bodyA ? hit.bodyB : hit.bodyA;
        const n = orientHitNormal(hit, ballId);
        const nLen = Math.hypot(n.x, n.y, n.z) || 1;
        consider({
          ballId,
          otherId,
          speed: hit.approachSpeed,
          point: { x: hit.point.x, y: hit.point.y, z: hit.point.z },
          dir: { x: n.x / nLen, y: n.y / nLen, z: n.z / nLen },
          hit,
          fromHit: true,
        });
      }
      continue;
    }
    const ballId = aIsBall ? hit.bodyA : hit.bodyB;
    const otherId = aIsBall ? hit.bodyB : hit.bodyA;
    const n = orientHitNormal(hit, ballId);
    const nLen = Math.hypot(n.x, n.y, n.z) || 1;
    consider({
      ballId,
      otherId,
      speed: hit.approachSpeed,
      point: { x: hit.point.x, y: hit.point.y, z: hit.point.z },
      dir: { x: n.x / nLen, y: n.y / nLen, z: n.z / nLen },
      hit,
      fromHit: true,
    });
  }

  // Begin fallback only when no hard-hit payload exists for that projectile.
  const considerBegin = (ballId: number, otherId: number, speed: number) => {
    if (bestByBall.has(ballId)) return;

    // Deterministic direction: ball → other from live poses, else into wall (−Z).
    let dx = 0;
    let dy = 0;
    let dz = -1;
    const ballPos = bodyPositionLive(rt, ballId) ?? bodyPosition(rt, ballId);
    const otherPos = bodyPositionLive(rt, otherId) ?? bodyPosition(rt, otherId);
    if (ballPos && otherPos) {
      dx = otherPos.x - ballPos.x;
      dy = otherPos.y - ballPos.y;
      dz = otherPos.z - ballPos.z;
      const L = Math.hypot(dx, dy, dz);
      if (L > 1e-6) {
        dx /= L;
        dy /= L;
        dz /= L;
      } else {
        dx = 0;
        dy = 0;
        dz = -1;
      }
    }
    const point = ballPos ?? otherPos;
    if (!point) return;

    consider({
      ballId,
      otherId,
      speed,
      point: { x: point.x, y: point.y, z: point.z },
      dir: { x: dx, y: dy, z: dz },
      hit: undefined,
      fromHit: false,
    });
  };

  for (const event of beginEvents) {
    const aIsBall = rt.cannonballSet.has(event.bodyA);
    const bIsBall = rt.cannonballSet.has(event.bodyB);
    if (!aIsBall && !bIsBall) continue;
    if (aIsBall && bIsBall) {
      considerBegin(event.bodyA, event.bodyB, event.approachSpeed);
      considerBegin(event.bodyB, event.bodyA, event.approachSpeed);
    } else {
      considerBegin(
        aIsBall ? event.bodyA : event.bodyB,
        aIsBall ? event.bodyB : event.bodyA,
        event.approachSpeed,
      );
    }
  }

  for (const cand of bestByBall.values()) {
    // Already stuck in structure: ignore further contacts until un-embed / TTL.
    if (rt.embeddedProjectiles.has(cand.ballId)) continue;

    const wall = classifyWallHit(rt, cand.otherId);
    const projectileContact = rt.cannonballSet.has(cand.otherId);
    const groundContact = cand.otherId === rt.groundBodyId;
    if (!wall.hitsWall && !projectileContact && !groundContact) continue;

    const weapon = rt.projectileWeapons.get(cand.ballId) ?? 'cannon';
    const impact: DemolitionImpact = {
      x: cand.point.x,
      y: cand.point.y,
      z: cand.point.z,
      speed: cand.speed,
    };

    // Fracture velocity = oriented normal × approachSpeed (never post-solver v).
    const vx = cand.dir.x * cand.speed;
    const vy = cand.dir.y * cand.speed;
    const vz = cand.dir.z * cand.speed;
    const hitPoint = { x: impact.x, y: impact.y, z: impact.z };
    const { radius: projectileRadius, mass: projectileMass } = projectileDamageMass(weapon);

    if (projectileContact) {
      queueImpactBurst(rt, hitPoint, cand.dir, cand.speed, weapon);
      pushImpactTrace(rt, {
        projectileBodyId: cand.ballId,
        contactBodyId: cand.otherId,
        contactKind: 'projectile',
        contactPoint: { ...hitPoint },
        approachSpeed: cand.speed,
        projectileRadius,
        projectileMass,
        energyTransfer: 0,
        appliedEnergy: 0,
        damageOrigin: { ...hitPoint },
        directReleases: [],
        supportReleases: [],
        carvedCellCount: 0,
        topologyChanged: false,
        weapon,
      });
      continue;
    }

    if (groundContact) {
      // Explosives arm where they land; kinetic shots get one landing trace
      // per ball so a clean through-hole shot is never silent.
      const groundPower = rt.projectilePowers.get(cand.ballId) ?? DEFAULT_WEAPON_POWER;
      if (weapon === 'bomb') {
        placeCharge(rt, hitPoint.x, hitPoint.y, hitPoint.z, groundPower);
        retireProjectile(rt, cand.ballId);
        continue;
      }
      if (weapon === 'concussion') {
        runBlastDetonation(
          rt,
          hitPoint.x,
          hitPoint.y,
          hitPoint.z,
          scaleDamageEventEnergy(concussionEvent(hitPoint), groundPower),
        );
        retireProjectile(rt, cand.ballId);
        continue;
      }
      if (!rt.projectileGroundTraced.has(cand.ballId)) {
        rt.projectileGroundTraced.add(cand.ballId);
        queueImpactBurst(rt, hitPoint, cand.dir, cand.speed, weapon);
        pushImpactTrace(rt, {
          projectileBodyId: cand.ballId,
          contactBodyId: cand.otherId,
          contactKind: 'ground',
          contactPoint: { ...hitPoint },
          approachSpeed: cand.speed,
          projectileRadius,
          projectileMass,
          energyTransfer: 0,
          appliedEnergy: 0,
          damageOrigin: { ...hitPoint },
          directReleases: [],
          supportReleases: [],
          carvedCellCount: 0,
          topologyChanged: false,
          weapon,
        });
      }
      continue;
    }

    const contactKind: ImpactContactKind = !rt.fractured
      ? 'monolith'
      : wall.hitStaticRemnant ? 'static' : 'dynamic';
    if (cand.speed < threshold) {
      // Below the structural gate the contact is still physical: a slow ball
      // nudges loose rubble, and the trace must carry the same kick telemetry
      // as full-speed rubble hits.
      let slowKickSpeed: number | undefined;
      if (contactKind === 'dynamic') {
        markRubbleActivity(rt, cand.otherId);
        slowKickSpeed = kickDynamicRubble(
          rt,
          cand.otherId,
          cand.dir,
          cand.speed,
          projectileSpecMass(weapon).mass,
        );
      }
      queueImpactBurst(rt, hitPoint, cand.dir, cand.speed, weapon);
      pushImpactTrace(rt, {
        projectileBodyId: cand.ballId,
        contactBodyId: cand.otherId,
        contactKind,
        contactPoint: { ...hitPoint },
        approachSpeed: cand.speed,
        projectileRadius,
        projectileMass,
        energyTransfer: 0,
        appliedEnergy: 0,
        rubbleKickSpeed: slowKickSpeed,
        belowStructuralGate: true,
        damageOrigin: { ...hitPoint },
        directReleases: [],
        supportReleases: [],
        carvedCellCount: 0,
        topologyChanged: false,
        weapon,
      });
      continue;
    }

    recordImpact(rt, impact);

    // Charges and concussion canisters are thrown to the wall, then become
    // an area event. The projectile body is removed so it cannot hit twice.
    const launchedPower = rt.projectilePowers.get(cand.ballId) ?? DEFAULT_WEAPON_POWER;
    if (weapon === 'bomb') {
      placeCharge(rt, hitPoint.x, hitPoint.y, hitPoint.z, launchedPower);
      retireProjectile(rt, cand.ballId);
      continue;
    }
    if (weapon === 'concussion') {
      runBlastDetonation(
        rt,
        hitPoint.x,
        hitPoint.y,
        hitPoint.z,
        scaleDamageEventEnergy(concussionEvent(hitPoint), launchedPower),
      );
      retireProjectile(rt, cand.ballId);
      continue;
    }

    const { mass: physicsProjectileMass } = projectileSpecMass(weapon);
    queueImpactBurst(rt, hitPoint, cand.dir, cand.speed, weapon);

    if (rt.fractured && !wall.hitStaticRemnant) {
      markRubbleActivity(rt, cand.otherId);
      const rubbleKickSpeed = kickDynamicRubble(
        rt,
        cand.otherId,
        cand.dir,
        cand.speed,
        physicsProjectileMass,
      );
      const inExpandedWall =
        hitPoint.x >= rt.wallCenter.x - rt.wallHalf.x - 0.5 &&
        hitPoint.x <= rt.wallCenter.x + rt.wallHalf.x + 0.5 &&
        hitPoint.y >= rt.wallCenter.y - rt.wallHalf.y - 0.5 &&
        hitPoint.y <= rt.wallCenter.y + rt.wallHalf.y + 0.5;
      // Linear falloff with standoff from the wall slab along z: contact on
      // the face transmits fully, a heap RUBBLE_TRANSMISSION_DEPTH out passes
      // nothing. Prevents the growing debris rampart from making the wall
      // unreachable while keeping transmission bounded and deterministic.
      const zStandoff = Math.max(
        0,
        Math.abs(hitPoint.z - rt.wallCenter.z) - rt.wallHalf.z,
      );
      const proximity = Math.max(0, 1 - zStandoff / RUBBLE_TRANSMISSION_DEPTH);
      let transmittedOutcome: CarveOutcome | null = null;
      let transmittedTransfer = 0;
      if (inExpandedWall && proximity > 0) {
        transmittedTransfer = kineticFractureTransfer(weapon);
        const baseEvent = weaponImpactEvent(
          weapon,
          hitPoint,
          { x: vx, y: vy, z: vz },
          transmittedTransfer,
        );
        const transmittedEvent = {
          ...baseEvent,
          energy: baseEvent.energy * RUBBLE_TRANSMISSION_TRANSFER * proximity,
        };
        transmittedOutcome = subsequentCarve(rt, impact, transmittedEvent, {
          preferredPrimaryCellId: wall.preferredCellId,
          subBudget: SUBSEQUENT_IMPACT_SUB_BUDGET,
        });
      }
      pushImpactTrace(rt, {
        projectileBodyId: cand.ballId,
        contactBodyId: cand.otherId,
        contactKind: 'dynamic',
        contactPoint: { ...hitPoint },
        approachSpeed: cand.speed,
        projectileRadius,
        projectileMass,
        energyTransfer: transmittedTransfer * RUBBLE_TRANSMISSION_TRANSFER,
        appliedEnergy: transmittedOutcome?.appliedEnergy ?? 0,
        rubbleKickSpeed,
        transmitted: transmittedOutcome ? true : undefined,
        preferredPrimaryCellId:
          transmittedOutcome?.preferredPrimaryCellId ?? wall.preferredCellId,
        damageOrigin: transmittedOutcome?.damageOrigin ?? { ...hitPoint },
        directReleases: transmittedOutcome?.directReleases ?? [],
        supportReleases: transmittedOutcome?.supportReleases ?? [],
        carvedCellCount: transmittedOutcome?.carvedCellIds.length ?? 0,
        topologyChanged: transmittedOutcome?.topologyChanged ?? false,
        weapon,
      });
      stabilizeProjectileContact(
        rt,
        cand.ballId,
        cand.otherId,
        weapon,
        cand.hit,
        cand.speed,
      );
      continue;
    }

    const transfer = kineticFractureTransfer(weapon);
    const dmgEvent = weaponImpactEvent(
      weapon,
      hitPoint,
      { x: vx, y: vy, z: vz },
      transfer,
    );

    let outcome: CarveOutcome;
    if (!rt.fractured) {
      outcome = firstFracture(rt, impact, dmgEvent);
    } else {
      outcome = subsequentCarve(rt, impact, dmgEvent, {
        preferredPrimaryCellId: wall.preferredCellId,
        subBudget: SUBSEQUENT_IMPACT_SUB_BUDGET,
      });
    }

    // Spike embeds in structure instead of bouncing out of the crater.
    let embedded: true | undefined;
    if (weapon === 'spike') {
      // Body may have been destroyed by the same-step pool eviction — only
      // embed when the handle still resolves.
      const stillLive = bodyPositionLive(rt, cand.ballId) != null;
      if (stillLive) {
        embedSpikeProjectile(rt, cand.ballId, hitPoint);
        if (rt.embeddedProjectiles.has(cand.ballId)) embedded = true;
      }
    }

    pushImpactTrace(rt, {
      projectileBodyId: cand.ballId,
      contactBodyId: cand.otherId,
      contactKind,
      contactPoint: { ...hitPoint },
      approachSpeed: cand.speed,
      projectileRadius,
      projectileMass,
      energyTransfer: transfer,
      appliedEnergy: outcome.appliedEnergy,
      preferredPrimaryCellId: outcome.preferredPrimaryCellId ?? wall.preferredCellId,
      damageOrigin: outcome.damageOrigin,
      directReleases: outcome.directReleases,
      supportReleases: outcome.supportReleases,
      carvedCellCount: outcome.carvedCellIds.length,
      topologyChanged: outcome.topologyChanged,
      embedded,
      weapon,
    });

    if (embedded) continue;

    // A static structural contact may destroy its own collision body. Give the
    // ball a deterministic exit from the wall face; loose rubble stays under
    // normal Box3D resolution plus the narrowly-scoped penetration recovery.
    ejectProjectileFromWallFace(rt, cand.ballId, weapon, hitPoint, cand.speed);
  }
}

/**
 * DEV/E2E probe: fire a cannonball from (origin) toward direction, or
 * teleport-impact at a world point by synthesizing a fracture event.
 */
export function fireAt(
  rt: DemolitionRuntime,
  x: number,
  y: number,
  z: number,
  dirX: number,
  dirY: number,
  dirZ: number,
) {
  // Spawn behind the target along -dir so the ball flies into the wall.
  const len = Math.hypot(dirX, dirY, dirZ) || 1;
  const ndx = dirX / len;
  const ndy = dirY / len;
  const ndz = dirZ / len;
  // Origin: back along -dir from the aim point by a few meters.
  const origin = {
    x: x - ndx * 8,
    y: y - ndy * 8,
    z: z - ndz * 8,
  };
  // Fire whatever tool is selected — hardcoding the cannon here meant the probe
  // (and therefore every weapon E2E built on it) silently tested cannon only.
  spawnProjectile(rt, origin, { x: ndx, y: ndy, z: ndz }, rt.tool);
}

/**
 * Deterministic fracture at a world point without waiting for ball contact.
 * Used by E2E when contact timing is unreliable under swiftshader.
 * Still records lastImpact and runs the real fracture/collapse path.
 *
 * Uses default energyTransfer=1 (full ½mv² at the given speed) so the probe
 * remains a pure damage-model / topology probe. Live processContacts applies
 * weapon-specific transfer (cannon = (28/55)²).
 */
export function probeImpact(
  rt: DemolitionRuntime,
  x: number,
  y: number,
  z: number,
  speed: number = 25,
  /** Incoming flight direction (toward the wall). Default head-on −Z. */
  dir: { x: number; y: number; z: number } = { x: 0, y: 0, z: -1 },
) {
  const impact: DemolitionImpact = { x, y, z, speed };
  recordImpact(rt, impact);
  const len = Math.hypot(dir.x, dir.y, dir.z) || 1;
  const nd = { x: dir.x / len, y: dir.y / len, z: dir.z / len };
  const velocity = { x: nd.x * speed, y: nd.y * speed, z: nd.z * speed };
  // Default transfer=1 — intentional probe/live distinction (see above).
  const dmgEvent = cannonballEvent({ x, y, z }, velocity);
  const contactKind: ImpactContactKind = !rt.fractured ? 'monolith' : 'static';
  const { radius: projectileRadius, mass: projectileMass } = projectileDamageMass('cannon');
  // Keep deterministic probe impacts on the same canonical presentation seam
  // as live contacts so visual captures exercise the real burst subscriber.
  const outcome = !rt.fractured
    ? firstFracture(rt, impact, dmgEvent)
    : subsequentCarve(rt, impact, dmgEvent);
  queueImpactBurst(rt, impact, nd, speed, 'cannon', rt.lastPulverizedVolume);
  pushImpactTrace(rt, {
    contactKind,
    contactPoint: { x, y, z },
    approachSpeed: speed,
    projectileRadius,
    projectileMass,
    energyTransfer: 1,
    appliedEnergy: outcome.appliedEnergy,
    preferredPrimaryCellId: outcome.preferredPrimaryCellId,
    damageOrigin: outcome.damageOrigin,
    directReleases: outcome.directReleases,
    supportReleases: outcome.supportReleases,
    carvedCellCount: outcome.carvedCellIds.length,
    topologyChanged: outcome.topologyChanged,
    weapon: 'cannon',
  });
}

// ---------------------------------------------------------------------------
// Bomb / explosion (D2.1)
// ---------------------------------------------------------------------------

/**
 * True when the charge is near the wall volume (carve target). Ground-near
 * rubble placements return false → impulse-only detonation.
 */
export function isBlastNearWall(
  rt: DemolitionRuntime,
  x: number,
  y: number,
  z: number,
): boolean {
  const half = rt.wallHalf;
  const c = rt.wallCenter;
  // Inflate slightly so front-face placement still counts as wall.
  const pad = 0.35;
  return (
    x >= c.x - half.x - pad &&
    x <= c.x + half.x + pad &&
    y >= c.y - half.y - pad &&
    y <= c.y + half.y + pad &&
    z >= c.z - half.z - pad &&
    z <= c.z + half.z + pad
  );
}

/**
 * Place a charge at world point. Returns the charge, or null if at cap.
 * Detonation times: first fuse BLAST_FUSE_SEC; multi-charge staggered by
 * BLAST_STAGGER_SEC in placement order.
 */
export function placeCharge(
  rt: DemolitionRuntime,
  x: number,
  y: number,
  z: number,
  power: number = rt.weaponPower,
): DemolitionCharge | null {
  const live = rt.charges.filter((c) => !c.detonated);
  if (live.length >= MAX_CHARGES) return null;

  // New volley (no live fuses) → reset shared sub-fracture budget.
  if (live.length === 0) {
    rt.volleySubBudget = BLAST_VOLLEY_SUB_BUDGET;
  }

  const simT = rt.stepper.simTime;
  let detonateAt = simT + BLAST_FUSE_SEC;
  // Stagger after the latest pending detonation (placement order).
  for (const c of rt.charges) {
    if (c.detonated) continue;
    detonateAt = Math.max(detonateAt, c.detonateSimTime + BLAST_STAGGER_SEC);
  }

  const charge: DemolitionCharge = {
    id: rt.nextChargeId++,
    x,
    y,
    z,
    placeSimTime: simT,
    detonateSimTime: detonateAt,
    power: clampWeaponPower(power),
    detonated: false,
  };
  rt.charges.push(charge);
  return charge;
}

/** Impulse-only sweep on all dynamic rubble + projectiles (ground blast). */
export function blastImpulseOnly(
  rt: DemolitionRuntime,
  impact: DemolitionImpact,
  blastEvent: DamageEvent = bombEvent({ x: impact.x, y: impact.y, z: impact.z }),
) {
  const profile = rt.profile;
  const bodies: BodyHandle[] = [];
  for (const rec of rt.records.values()) {
    if (rec.state !== 'dynamic' || !rec.bodyId) continue;
    bodies.push(rec.bodyId as BodyHandle);
  }
  for (const ball of rt.cannonballs) {
    bodies.push(ball as BodyHandle);
  }
  if (bodies.length === 0) return;
  // Energy-derived omni kick on existing rubble (no carve).
  // Match resolveDamageCore omni path: BLAST_KINETIC_MUL then BLAST_MAX_LAUNCH cap
  // so volley re-hits on already-dynamic chunks cannot stack past 8 m/s.
  const kineticPool =
    (1 - profile.physics.brittleness) *
    Math.min(blastEvent.energy * BLAST_KINETIC_MUL, IMPULSE_ENERGY_CAP_J);
  const blastRadius = blastEvent.footprint.kind === 'sphere' ? blastEvent.footprint.radius : 2.6;
  const blastCore = Math.max(blastRadius / 3, 0.15);
  rebuildBuffers(rt);
  for (const bodyId of bodies) {
    const pos = bodyPositionLive(rt, bodyId) ?? bodyPosition(rt, bodyId);
    if (!pos) continue;
    const dx = pos.x - impact.x;
    const dy = pos.y - impact.y;
    const dz = pos.z - impact.z;
    const d = Math.hypot(dx, dy, dz) || 1e-6;
    if (d > blastRadius) continue;
    const fall = 1 / (1 + (d / blastCore) * (d / blastCore));
    // Approximate mass 50 kg for debris kick magnitude.
    const eK = (kineticPool * fall) / Math.max(bodies.length, 1);
    const speed = Math.min(BLAST_MAX_LAUNCH, Math.sqrt((2 * eK) / 50));
    const inv = 1 / d;
    try {
      rt.world.setLinearVelocity(bodyId, [
        dx * inv * speed,
        dy * inv * speed + profile.impulse.upwardBias * speed,
        dz * inv * speed,
      ]);
      try {
        rt.world.wakeBody(bodyId);
      } catch {
        // ignore
      }
    } catch {
      // ignore
    }
  }
  clampLaunchSpeeds(rt, bodies, BLAST_MAX_LAUNCH);
  rt.lastActivitySimTime = rt.stepper.simTime;
}

export function scaleDamageEventEnergy(event: DamageEvent, power: number): DamageEvent {
  return { ...event, energy: event.energy * clampWeaponPower(power) };
}

/**
 * Sphere footprint radius × Hopkinson–Cranz scale for clamped charge power.
 * Only spheres — focused/point kernels are untouched. chargeEnergyScale must
 * never feed this: material yield ≠ physical reach.
 */
export function scaleBombSphereRadius(event: DamageEvent, clampedPower: number): DamageEvent {
  if (event.footprint.kind !== 'sphere') return event;
  const scale = blastRadiusScale(clampedPower);
  if (scale === 1) return event;
  return {
    ...event,
    footprint: { ...event.footprint, radius: event.footprint.radius * scale },
  };
}

export function runBlastDetonation(
  rt: DemolitionRuntime,
  x: number,
  y: number,
  z: number,
  blastEvent: DamageEvent = bombEvent({ x, y, z }),
  /** Clamped charge power → omni launch floor. Default 1 = identity. */
  launchScale: number = 1,
) {
  const impact: DemolitionImpact = {
    x,
    y,
    z,
    speed: BLAST_EQUIV_SPEED,
  };
  recordImpact(rt, impact);
  rt.lastBlast = { x, y, z };

  const dmgEvent = blastEvent;
  const shockwaveRadius = blastEvent.footprint.kind === 'sphere' ? blastEvent.footprint.radius : 2.6;
  // Shockwave VFX for the page (radius used for ring scale).
  appendDemolitionEvent(rt, 'shockwave', { x, y, z, radius: shockwaveRadius });

  // Seed a few ballistic shards at the epicenter (micro-debris burst).
  const burstN = 8;
  for (let i = 0; i < burstN; i += 1) {
    const a = (i / burstN) * Math.PI * 2;
    const sp = 2.5 + (i % 3) * 0.8;
    appendDemolitionEvent(rt, 'shard', {
      x: x + Math.cos(a) * 0.05,
      y: y + 0.05,
      z: z + Math.sin(a) * 0.05,
      vx: Math.cos(a) * sp,
      vy: 1.5 + (i % 4) * 0.4,
      vz: Math.sin(a) * sp,
      volume: 0.0004,
      material: rt.profile.id,
    });
  }

  const nearWall = isBlastNearWall(rt, x, y, z);
  if (!nearWall) {
    // Ground / rubble charge — impulse only.
    blastImpulseOnly(rt, impact, blastEvent);
    return;
  }

  if (!rt.fractured) {
    firstFracture(rt, impact, dmgEvent, { launchScale });
  } else {
    subsequentCarve(rt, impact, dmgEvent, { launchScale });
    // If subsequent carve no-ops (glass impact-timed), still impulse existing shards.
    if (rt.profile.partitionTiming === 'impact') {
      blastImpulseOnly(rt, impact, blastEvent);
    }
  }
}

export function processCharges(rt: DemolitionRuntime) {
  if (rt.charges.length === 0) return;
  const simT = rt.stepper.simTime;
  for (const c of rt.charges) {
    if (c.detonated) continue;
    if (simT < c.detonateSimTime) continue;
    c.detonated = true;
    // Power clamp first, material scale after. Radius uses clamped power only
    // (not chargeEnergyScale) so material identity never changes blast reach.
    const clampedPower = clampWeaponPower(c.power);
    const powered = scaleBombSphereRadius(
      scaleDamageEventEnergy(bombEvent({ x: c.x, y: c.y, z: c.z }), clampedPower),
      clampedPower,
    );
    const event = {
      ...powered,
      energy: powered.energy * rt.profile.chargeEnergyScale,
    };
    runBlastDetonation(rt, c.x, c.y, c.z, event, clampedPower);
  }
  // F4: drop detonated charges immediately so markers clear and charges===0
  // after volley completion (not only when the whole array finishes together).
  rt.charges = rt.charges.filter((c) => !c.detonated);
}

/**
 * DEV/E2E: place + immediately detonate (no fuse) at a world point.
 */
export function probeDetonate(
  rt: DemolitionRuntime,
  x: number,
  y: number,
  z: number,
  power: number = rt.weaponPower,
) {
  // Ensure volley budget is fresh for a single-shot detonation.
  if (rt.charges.filter((c) => !c.detonated).length === 0) {
    rt.volleySubBudget = BLAST_VOLLEY_SUB_BUDGET;
  }
  // Same separation as processCharges: power clamp first, material scale after.
  // Radius + launch floor both key off clamped power only.
  const clampedPower = clampWeaponPower(power);
  const powered = scaleBombSphereRadius(
    scaleDamageEventEnergy(bombEvent({ x, y, z }), clampedPower),
    clampedPower,
  );
  const event = {
    ...powered,
    energy: powered.energy * rt.profile.chargeEnergyScale,
  };
  runBlastDetonation(rt, x, y, z, event, clampedPower);
}
